import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  CardHeaderToolbar,
} from "../../../../_metronic/_partials/controls";
import { alertError, alertSuccess } from "../../../../utils/logger";
import {
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  TextField,
  Grid,
  Paper,
  Typography,
  Box,
  IconButton,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { getBestsellers, updateBestseller, getRelojes } from "../../../../api/reloj";
import { SERVER_URL } from "../../../../api";

const useStyles = makeStyles((theme) => ({
  positionCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    minHeight: "200px",
  },
  positionNumber: {
    fontSize: "18px",
    fontWeight: "bold",
    marginBottom: theme.spacing(1),
  },
  watchImage: {
    width: "100%",
    maxWidth: "150px",
    height: "150px",
    objectFit: "cover",
    borderRadius: "4px",
    marginBottom: theme.spacing(1),
    backgroundColor: "#f5f5f5",
  },
  watchInfo: {
    marginTop: theme.spacing(1),
  },
  emptySlot: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "150px",
    border: "2px dashed #ccc",
    borderRadius: "4px",
    color: "#999",
    backgroundColor: "#fafafa",
  },
  optionContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 0),
  },
  optionImage: {
    width: "50px",
    height: "50px",
    objectFit: "cover",
    borderRadius: "4px",
    flexShrink: 0,
  },
  optionText: {
    flex: 1,
    minWidth: 0,
  },
}));

function BestsellersPage() {
  const classes = useStyles();
  const [bestsellers, setBestsellers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState({});
  const [searchOptions, setSearchOptions] = useState({});
  const [searchLoading, setSearchLoading] = useState({});
  const searchTimeouts = useRef({});

  useEffect(() => {
    loadBestsellers();
  }, []);

  const loadBestsellers = async () => {
    try {
      setLoading(true);
      const response = await getBestsellers();
      if (response?.data) {
        // Crear un array de 12 posiciones
        const positions = Array.from({ length: 12 }, (_, i) => {
          const pos = i + 1;
          const bestseller = response.data.find((b) => b.bestsellerPosition === pos);
          return bestseller || { position: pos, empty: true };
        });
        setBestsellers(positions);
      }
    } catch (error) {
      alertError({ error, customMessage: "Error al cargar bestsellers" });
    } finally {
      setLoading(false);
    }
  };

  const searchWatches = useCallback(async (searchTerm, position) => {
    // Limpiar timeout anterior para esta posición
    if (searchTimeouts.current[position]) {
      clearTimeout(searchTimeouts.current[position]);
    }

    if (!searchTerm || searchTerm.length < 2) {
      setSearchOptions((prev) => {
        const newOptions = { ...prev };
        delete newOptions[position];
        return newOptions;
      });
      setSearchLoading((prev) => ({ ...prev, [position]: false }));
      return;
    }

    // Debounce: esperar 300ms antes de buscar
    searchTimeouts.current[position] = setTimeout(async () => {
      try {
        setSearchLoading((prev) => ({ ...prev, [position]: true }));
        // Usar los mismos filtros que la tienda web para encontrar los mismos relojes
        // Forzar showAll="false" como string para que el backend lo respete
        // Esto asegura que incluso siendo admin, se muestren solo los relojes de la tienda
        const response = await getRelojes({
          filters: {
            q: searchTerm.trim(),
            showAll: "false", // Forzar filtros de tienda web (active, no pendientes, no vendidos)
            origin: "not-manual", // Excluir relojes manuales (como en la tienda)
            noGroup: "true", // No agrupar para mostrar todos los relojes individuales
          },
          page: 1,
          limit: 50, // Aumentar límite para más resultados
        });
        console.log(`[BESTSELLER SEARCH] Search term: "${searchTerm}", Results:`, response?.data);
        if (response?.data?.data && Array.isArray(response.data.data)) {
          setSearchOptions((prev) => ({ ...prev, [position]: response.data.data }));
        } else if (response?.data && Array.isArray(response.data)) {
          setSearchOptions((prev) => ({ ...prev, [position]: response.data }));
        } else {
          console.warn(`[BESTSELLER SEARCH] Unexpected response format:`, response?.data);
          setSearchOptions((prev) => ({ ...prev, [position]: [] }));
        }
      } catch (error) {
        console.error("[BESTSELLER SEARCH] Error searching watches:", error);
        console.error("[BESTSELLER SEARCH] Error details:", error.response?.data || error.message);
        setSearchOptions((prev) => ({ ...prev, [position]: [] }));
      } finally {
        setSearchLoading((prev) => ({ ...prev, [position]: false }));
      }
    }, 300);
  }, []);

  const handleSelectWatch = async (position, watch) => {
    if (!watch || !watch._id) return;

    try {
      setSaving((prev) => ({ ...prev, [position]: true }));

      // Si hay otro reloj en esta posición, quitarle la posición
      const currentBestseller = bestsellers[position - 1];
      if (currentBestseller && currentBestseller._id && !currentBestseller.empty) {
        await updateBestseller(currentBestseller._id, {
          bestseller: false,
          bestsellerPosition: null,
        });
      }

      // Asignar el nuevo reloj a esta posición
      await updateBestseller(watch._id, {
        bestseller: true,
        bestsellerPosition: position,
      });

      // Actualizar el estado local inmediatamente para evitar parpadeo
      setBestsellers((prev) => {
        const newBestsellers = [...prev];
        newBestsellers[position - 1] = { ...watch, bestsellerPosition: position };
        return newBestsellers;
      });

      // Limpiar la búsqueda para esta posición
      setSearchOptions((prev) => {
        const newOptions = { ...prev };
        delete newOptions[position];
        return newOptions;
      });

      alertSuccess("Bestseller actualizado correctamente");
      // Recargar en segundo plano sin mostrar loading
      loadBestsellers();
    } catch (error) {
      alertError({ error, customMessage: "Error al actualizar bestseller" });
    } finally {
      setSaving((prev) => ({ ...prev, [position]: false }));
    }
  };

  const handleRemoveWatch = async (position, watchId) => {
    try {
      setSaving((prev) => ({ ...prev, [position]: true }));
      await updateBestseller(watchId, {
        bestseller: false,
        bestsellerPosition: null,
      });
      
      // Actualizar el estado local inmediatamente
      setBestsellers((prev) => {
        const newBestsellers = [...prev];
        newBestsellers[position - 1] = { position, empty: true };
        return newBestsellers;
      });

      alertSuccess("Bestseller eliminado correctamente");
      // Recargar en segundo plano
      loadBestsellers();
    } catch (error) {
      alertError({ error, customMessage: "Error al eliminar bestseller" });
    } finally {
      setSaving((prev) => ({ ...prev, [position]: false }));
    }
  };

  const buildImageUrl = (imagePath) => {
    if (!imagePath) return null;
    
    // Si ya es una URL completa (http/https), devolverla tal cual
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    
    // Normalizar SERVER_URL (eliminar trailing slash si existe)
    const normalizedServerUrl = SERVER_URL?.endsWith('/') ? SERVER_URL.slice(0, -1) : SERVER_URL;
    
    // Si empieza con /uploads, concatenar directamente con SERVER_URL
    if (imagePath.startsWith('/uploads')) {
      return `${normalizedServerUrl}${imagePath}`;
    }
    
    // Si empieza con uploads/ (sin / inicial), agregar / antes
    if (imagePath.startsWith('uploads/')) {
      return `${normalizedServerUrl}/${imagePath}`;
    }
    
    // Si no tiene / al inicio, agregarlo
    if (!imagePath.startsWith('/')) {
      return `${normalizedServerUrl}/${imagePath}`;
    }
    
    // Si ya tiene / al inicio, concatenar directamente
    return `${normalizedServerUrl}${imagePath}`;
  };

  return (
    <>
      <Card>
        <CardHeader title="Gestionar Más Vendidos">
          <CardHeaderToolbar>
            <Button
              variant="contained"
              color="primary"
              onClick={loadBestsellers}
              disabled={loading}
            >
              Recargar
            </Button>
          </CardHeaderToolbar>
        </CardHeader>
        <CardBody>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={3}>
              {bestsellers.map((item, index) => {
                const position = index + 1;
                const watch = item.empty ? null : item;
                const isSaving = saving[position];

                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={position}>
                    <Paper className={classes.positionCard}>
                      <Typography className={classes.positionNumber}>
                        Posición {position}
                      </Typography>

                      {watch && !watch.empty ? (
                        <>
                          <Box display="flex" justifyContent="center" mb={1}>
                            {watch.images && watch.images[0] ? (
                              <img
                                src={buildImageUrl(watch.images[0])}
                                alt={watch.name || watch.sku}
                                className={classes.watchImage}
                                onError={(e) => {
                                  console.error("Error loading image:", buildImageUrl(watch.images[0]), watch.images[0]);
                                  e.target.style.display = "none";
                                }}
                                onLoad={() => {
                                  console.log("Image loaded successfully:", buildImageUrl(watch.images[0]));
                                }}
                              />
                            ) : (
                              <Box
                                className={classes.watchImage}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                bgcolor="#f5f5f5"
                              >
                                <Typography variant="caption" color="textSecondary">
                                  Sin imagen
                                </Typography>
                              </Box>
                            )}
                          </Box>
                          <div className={classes.watchInfo}>
                            <Typography variant="body2" noWrap>
                              <strong>{watch.brand?.nombre || "N/A"}</strong>
                            </Typography>
                            <Typography variant="body2" noWrap>
                              {watch.name || watch.sku}
                            </Typography>
                            {watch.price?.amount && (
                              <Typography variant="body2" color="textSecondary">
                                {new Intl.NumberFormat("es-ES", {
                                  style: "currency",
                                  currency: "EUR",
                                }).format(watch.price.amount)}
                              </Typography>
                            )}
                          </div>
                          <Button
                            variant="outlined"
                            color="secondary"
                            size="small"
                            fullWidth
                            onClick={() => handleRemoveWatch(position, watch._id)}
                            disabled={isSaving}
                            style={{ marginTop: "8px" }}
                          >
                            {isSaving ? "Eliminando..." : "Eliminar"}
                          </Button>
                        </>
                      ) : (
                        <div className={classes.emptySlot}>
                          <Typography variant="body2" color="textSecondary">
                            Vacío
                          </Typography>
                        </div>
                      )}

                      <Autocomplete
                        options={searchOptions[position] || []}
                        getOptionLabel={(option) =>
                          `${option.brand?.nombre || ""} ${option.name || option.sku || ""}`
                        }
                        loading={searchLoading[position] || false}
                        onInputChange={(event, newInputValue) => {
                          searchWatches(newInputValue, position);
                        }}
                        onChange={(event, newValue) => {
                          if (newValue) {
                            handleSelectWatch(position, newValue);
                          }
                        }}
                        value={null}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Buscar reloj"
                            variant="outlined"
                            size="small"
                            fullWidth
                            style={{ marginTop: "8px" }}
                          />
                        )}
                        renderOption={(option) => (
                          <div className={classes.optionContainer}>
                            {option.images && option.images[0] ? (
                              <img
                                src={buildImageUrl(option.images[0])}
                                alt={option.name || option.sku}
                                className={classes.optionImage}
                                onError={(e) => {
                                  console.error("Error loading option image:", buildImageUrl(option.images[0]), option.images[0]);
                                  e.target.style.display = "none";
                                }}
                              />
                            ) : (
                              <Box
                                width="50px"
                                height="50px"
                                bgcolor="#f5f5f5"
                                borderRadius="4px"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                flexShrink={0}
                              >
                                <Typography variant="caption" color="textSecondary" style={{ fontSize: "10px" }}>
                                  Sin img
                                </Typography>
                              </Box>
                            )}
                            <div className={classes.optionText}>
                              <Typography variant="body2" noWrap>
                                <strong>{option.brand?.nombre || "N/A"}</strong> - {option.name || option.sku}
                              </Typography>
                              {option.price?.amount && (
                                <Typography variant="caption" color="textSecondary">
                                  {new Intl.NumberFormat("es-ES", {
                                    style: "currency",
                                    currency: "EUR",
                                  }).format(option.price.amount)}
                                </Typography>
                              )}
                            </div>
                          </div>
                        )}
                      />
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </CardBody>
      </Card>
    </>
  );
}

export default BestsellersPage;

